import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTrigger,
  EmptyState,
  ErrorState,
  Field,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SkeletonTable,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeftRight,
  ClipboardList,
  FileSpreadsheet,
  Link2,
  Package,
  PencilLine,
  Plus,
  UploadCloud,
  Warehouse,
} from "lucide-react";
import { useState } from "react";
import { CopyButton } from "../../components/copy-button";
import { ListSearch } from "../../components/list-search";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { skuOnChannelLabel, useChannels } from "../../lib/channel";
import { listCardClassName } from "../../lib/list-card";
import { matchesListQuery } from "../../lib/list-query";
import { formatBRL, formatDateTime, parseBRLAmount } from "../../lib/format";
import { formatDelta, skuLinkClassName } from "../../lib/stock";

type StockSearch = {
  q?: string;
};

export const Route = createFileRoute("/_app/stock")({
  validateSearch: (search: Record<string, unknown>): StockSearch => ({
    q: typeof search.q === "string" && search.q.trim() ? search.q : undefined,
  }),
  staticData: { layout: "wide" },
  component: StockPage,
});

function useWarehouses(enabled = true) {
  return useQuery({
    queryKey: ["warehouses"],
    queryFn: async () => unwrap(await api.warehouses.get()),
    enabled,
  });
}

function StockPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const skus = useQuery({
    queryKey: ["skus"],
    queryFn: async () => unwrap(await api.skus.get()),
  });
  const rows = skus.data?.skus ?? [];
  const visible = rows.filter((sku) =>
    matchesListQuery(search.q, [sku.code, sku.title, sku.supplier]),
  );

  return (
    <>
      <PageHeader
        title="Estoque"
        description={
          search.q
            ? `${visible.length} de ${rows.length} SKUs`
            : "SKUs centrais, cobertura em 30 dias (depósito + Full) e prazo de reposição"
        }
        actions={
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <CountDialog />
            <TransferDialog />
            <ImportCsvDialog />
            <WarehousesDialog />
            <CreateSkuDialog />
          </div>
        }
      />

      <Card>
        {skus.isLoading ? (
          <SkeletonTable rows={10} columns={6} />
        ) : skus.isError ? (
          <ErrorState
            title="Não foi possível carregar o estoque"
            error={skus.error}
            retrying={skus.isFetching}
            onRetry={() => void skus.refetch()}
          />
        ) : rows.length > 0 ? (
          <>
            <div className="flex justify-end border-b border-line px-3 py-2">
              <ListSearch
                value={search.q ?? ""}
                placeholder="Código, produto ou fornecedor"
                onValueChange={(value) =>
                  void navigate({
                    search: (prev) => ({
                      ...prev,
                      q: value.trim() || undefined,
                    }),
                  })
                }
              />
            </div>
            {visible.length === 0 ? (
              <EmptyState
                icon={<Package />}
                title="Nenhum SKU nesta busca"
                description="Tente o código, o título ou o fornecedor."
              />
            ) : (
              <>
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>SKU</TableHead>
                        <TableHead>Produto</TableHead>
                        <TableHead className="text-right">Custo</TableHead>
                        <TableHead className="text-right">Quantidade</TableHead>
                        <TableHead className="text-right">Cobertura</TableHead>
                        <TableHead className="text-right">Anúncios vinculados</TableHead>
                        <TableHead className="text-right">Atualizado</TableHead>
                        <TableHead className="w-32" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visible.map((sku) => (
                        <SkuRow key={sku.id} sku={sku} />
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <ul className="divide-y divide-line md:hidden">
                  {visible.map((sku) => (
                    <SkuCard key={sku.id} sku={sku} />
                  ))}
                </ul>
              </>
            )}
          </>
        ) : (
          <EmptyState
            icon={<Package />}
            title="Nenhum SKU cadastrado"
            description="Cadastre um SKU central e vincule os anúncios de cada conta para sincronizar o estoque automaticamente."
            action={<CreateSkuDialog />}
          />
        )}
      </Card>
    </>
  );
}

type SkuStock = {
  warehouseId: string;
  warehouseName: string;
  quantity: number;
};

type Sku = {
  id: string;
  code: string;
  title: string;
  cost: string | null;
  leadTimeDays: number;
  supplier?: string | null;
  totalQuantity: number;
  fullQuantity?: number;
  soldLast30Days: number;
  coverageDays: number | null;
  stocks: SkuStock[];
  updatedAt: string | Date;
  mappingCount: number;
};

function formatCoverage(days: number | null) {
  if (days == null) return "—";
  const rounded = days >= 10 ? Math.round(days) : Math.round(days * 10) / 10;
  return `${rounded}d`;
}

function coverageIsShort(sku: Sku) {
  const fullQuantity = sku.fullQuantity ?? 0;
  return (
    sku.coverageDays != null &&
    sku.coverageDays < sku.leadTimeDays &&
    sku.totalQuantity + fullQuantity > 0
  );
}

function SkuToolbar({ sku }: { sku: Sku }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const push = useMutation({
    mutationFn: async () => unwrap(await api.skus({ id: sku.id }).push.post()),
    onSuccess: () => {
      toast.add({
        type: "success",
        title: "Sincronização iniciada",
        description: `O estoque do SKU ${sku.code} será enviado aos anúncios vinculados.`,
      });
      void queryClient.invalidateQueries({ queryKey: ["sync-logs"] });
    },
  });

  return (
    <div className="flex items-center justify-end gap-1">
      <AdjustStockDialog sku={sku} />
      <MapListingDialog sku={sku} />
      <Tooltip content="Enviar estoque aos anúncios">
        <Button
          variant="ghost"
          size="icon"
          loading={push.isPending}
          onClick={() => push.mutate()}
        >
          <UploadCloud />
        </Button>
      </Tooltip>
    </div>
  );
}

function SkuCard({ sku }: { sku: Sku }) {
  const fullQuantity = sku.fullQuantity ?? 0;
  const short = coverageIsShort(sku);

  return (
    <li>
      <Link
        to="/stock/$id"
        params={{ id: sku.id }}
        className={listCardClassName}
      >
        <div className="flex items-start justify-between gap-3">
          <p className="font-mono text-[0.8125rem] font-medium text-ink">
            {sku.code}
          </p>
          {short ? (
            <Badge variant="warning" dot>
              {formatCoverage(sku.coverageDays)}
            </Badge>
          ) : (
            <span className="font-mono text-2xs text-ink-muted">
              {formatCoverage(sku.coverageDays)}
            </span>
          )}
        </div>
        <p className="mt-1 truncate text-2xs text-ink-secondary">{sku.title}</p>
        <p className="mt-1 text-2xs text-ink-faint">
          {sku.totalQuantity} un.
          {fullQuantity > 0 ? ` · ${fullQuantity} Full` : ""}
          {sku.cost != null ? ` · ${formatBRL(sku.cost)}` : ""}
          {` · ${sku.mappingCount} anúncio${sku.mappingCount === 1 ? "" : "s"}`}
        </p>
      </Link>
      <div className="-mt-1 flex justify-end px-3 pb-2">
        <SkuToolbar sku={sku} />
      </div>
    </li>
  );
}

function SkuRow({ sku }: { sku: Sku }) {
  const fullQuantity = sku.fullQuantity ?? 0;
  const warehouseHint =
    sku.stocks.length > 1
      ? sku.stocks.map((s) => `${s.warehouseName}: ${s.quantity}`).join(" · ")
      : sku.stocks[0]
        ? `${sku.stocks[0].warehouseName}: ${sku.stocks[0].quantity}`
        : `Depósito: ${sku.totalQuantity}`;
  const quantityHint = fullQuantity
    ? `${warehouseHint} · Full: ${fullQuantity}`
    : warehouseHint;

  return (
    <TableRow>
      <TableCell>
        <span className="inline-flex items-center gap-1 font-mono text-2xs font-medium text-ink">
          <Link
            to="/stock/$id"
            params={{ id: sku.id }}
            className={skuLinkClassName}
          >
            {sku.code}
          </Link>
          <CopyButton value={sku.code} label="Copiar SKU" />
        </span>
      </TableCell>
      <TableCell className="min-w-0 max-w-md truncate" title={sku.title}>
        {sku.title}
      </TableCell>
      <TableCell className="text-right font-mono text-ink">
        {sku.cost != null ? formatBRL(sku.cost) : (
          <span className="text-ink-muted">—</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <Tooltip content={quantityHint}>
          <span className="inline-flex items-center justify-end gap-1.5">
            <span className="font-mono text-ink">{sku.totalQuantity}</span>
            {fullQuantity > 0 && (
              <Badge variant="accent">{fullQuantity} Full</Badge>
            )}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell className="text-right">
        <Tooltip
          content={
            sku.soldLast30Days > 0
              ? `${sku.soldLast30Days} un. em 30d · reposição ${sku.leadTimeDays}d${
                  fullQuantity > 0 ? " · inclui estoque Full" : ""
                }`
              : `Sem venda em 30d · reposição ${sku.leadTimeDays}d`
          }
        >
          {coverageIsShort(sku) ? (
            <Badge variant="warning" dot>
              {formatCoverage(sku.coverageDays)}
            </Badge>
          ) : (
            <span className="font-mono text-ink-muted">
              {formatCoverage(sku.coverageDays)}
            </span>
          )}
        </Tooltip>
      </TableCell>
      <TableCell className="text-right">
        <Badge variant={sku.mappingCount > 0 ? "accent" : "neutral"}>
          {sku.mappingCount}
        </Badge>
      </TableCell>
      <TableCell className="text-right text-2xs text-ink-muted">
        {formatDateTime(sku.updatedAt)}
      </TableCell>
      <TableCell>
        <SkuToolbar sku={sku} />
      </TableCell>
    </TableRow>
  );
}

function WarehousesDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const warehouses = useWarehouses(open);
  const queryClient = useQueryClient();
  const toast = useToast();

  const create = useMutation({
    mutationFn: async () => unwrap(await api.warehouses.post({ name })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["warehouses"] });
      toast.add({ type: "success", title: `Depósito "${name}" criado` });
      setName("");
    },
    onError: () => {
      toast.add({
        type: "error",
        title: "Não foi possível criar",
        description: "Já existe um depósito com este nome.",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button variant="secondary">
            <Warehouse />
            Depósitos
          </Button>
        }
      />
      <DialogContent
        title="Depósitos"
        description="Cada anúncio vinculado consome o saldo de um depósito. Crie um por local físico de estoque."
      >
        <div className="flex flex-col gap-4">
          {warehouses.isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner className="size-4 text-ink-muted" />
            </div>
          ) : (
            <ul className="flex flex-col divide-y divide-line rounded-control border border-line">
              {warehouses.data?.warehouses.map((warehouse, index) => (
                <li
                  key={warehouse.id}
                  className="flex items-center justify-between px-3 py-2 text-xs"
                >
                  <span className="font-medium text-ink">{warehouse.name}</span>
                  {index === 0 && <Badge variant="neutral">padrão</Badge>}
                </li>
              ))}
            </ul>
          )}
          <form
            className="flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim()) create.mutate();
            }}
          >
            <Field className="flex-1">
              <FieldLabel>Novo depósito</FieldLabel>
              <Input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Filial Centro"
              />
            </Field>
            <Button type="submit" variant="secondary" loading={create.isPending}>
              <Plus />
              Criar
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WarehouseSelect({
  warehouses,
  value,
  onChange,
}: {
  warehouses: { id: string; name: string }[];
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  return (
    <Select
      value={value}
      itemToStringLabel={(id) =>
        warehouses.find((warehouse) => warehouse.id === id)?.name ??
        "Selecione um depósito"
      }
      onValueChange={(v) => onChange(v as string | null)}
    >
      <SelectTrigger placeholder="Selecione um depósito" className="w-full" />
      <SelectContent>
        {warehouses.map((warehouse) => (
          <SelectItem key={warehouse.id} value={warehouse.id}>
            {warehouse.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function CreateSkuDialog() {
  const channels = useChannels();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [title, setTitle] = useState("");
  const [quantity, setQuantity] = useState("0");
  const [cost, setCost] = useState("");
  const [leadTimeDays, setLeadTimeDays] = useState("7");
  const [supplier, setSupplier] = useState("");
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const warehouses = useWarehouses(open);
  const queryClient = useQueryClient();
  const toast = useToast();

  const warehouseOptions = warehouses.data?.warehouses ?? [];
  const effectiveWarehouseId = warehouseId ?? warehouseOptions[0]?.id ?? null;

  const create = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.skus.post({
          code,
          title,
          quantity: Number(quantity) || 0,
          warehouseId: effectiveWarehouseId ?? undefined,
          cost: parseBRLAmount(cost),
          leadTimeDays: Number(leadTimeDays) || 7,
          supplier: supplier || null,
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["skus"] });
      void queryClient.invalidateQueries({ queryKey: ["purchases"] });
      toast.add({ type: "success", title: `SKU ${code} cadastrado` });
      setOpen(false);
      setCode("");
      setTitle("");
      setQuantity("0");
      setCost("");
      setLeadTimeDays("7");
      setSupplier("");
      setWarehouseId(null);
    },
    onError: () => {
      toast.add({
        type: "error",
        title: "Não foi possível cadastrar",
        description: "Verifique se já não existe um SKU com este código.",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button>
            <Plus />
            Novo SKU
          </Button>
        }
      />
      <DialogContent
        title="Novo SKU"
        description={
          channels.providers.length === 1
            ? `O código deve ser o mesmo usado no campo ${skuOnChannelLabel(channels.providers[0])} dos anúncios.`
            : "O código deve ser o mesmo usado no campo SKU dos anúncios de cada canal."
        }
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <Field>
            <FieldLabel>Código</FieldLabel>
            <Input
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="CAM-PRETA-M"
              className="font-mono"
            />
          </Field>
          <Field>
            <FieldLabel>Descrição</FieldLabel>
            <Input
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Camiseta preta — M"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field>
              <FieldLabel>Custo unitário (R$)</FieldLabel>
              <Input
                inputMode="decimal"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="Opcional"
              />
            </Field>
            <Field>
              <FieldLabel>Quantidade inicial</FieldLabel>
              <Input
                type="number"
                min="0"
                required
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel>Depósito</FieldLabel>
              <WarehouseSelect
                warehouses={warehouseOptions}
                value={effectiveWarehouseId}
                onChange={setWarehouseId}
              />
            </Field>
            <Field>
              <FieldLabel>Prazo de reposição (dias)</FieldLabel>
              <Input
                type="number"
                min="1"
                max="90"
                value={leadTimeDays}
                onChange={(e) => setLeadTimeDays(e.target.value)}
              />
            </Field>
            <Field className="col-span-2">
              <FieldLabel>Fornecedor (opcional)</FieldLabel>
              <Input
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                placeholder="Para agrupar a lista de compras"
                maxLength={80}
              />
            </Field>
          </div>
          <Button type="submit" loading={create.isPending}>
            Cadastrar
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AdjustStockDialog({ sku }: { sku: Sku }) {
  const [open, setOpen] = useState(false);
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<string | null>(null);
  const [cost, setCost] = useState(
    sku.cost != null ? String(sku.cost).replace(".", ",") : "",
  );
  const [leadTimeDays, setLeadTimeDays] = useState(String(sku.leadTimeDays ?? 7));
  const [supplier, setSupplier] = useState(sku.supplier ?? "");
  const warehouses = useWarehouses(open);
  const queryClient = useQueryClient();
  const toast = useToast();

  const warehouseOptions = warehouses.data?.warehouses ?? [];
  const effectiveWarehouseId = warehouseId ?? warehouseOptions[0]?.id ?? null;
  const currentQuantity =
    sku.stocks.find((s) => s.warehouseId === effectiveWarehouseId)?.quantity ??
    0;
  const effectiveQuantity = quantity ?? String(currentQuantity);

  const adjust = useMutation({
    mutationFn: async () => {
      await unwrap(
        await api.skus({ id: sku.id }).patch({
          cost: parseBRLAmount(cost),
          leadTimeDays: Number(leadTimeDays) || 7,
          supplier: supplier || null,
        }),
      );
      return unwrap(
        await api.skus({ id: sku.id }).stock.put({
          quantity: Number(effectiveQuantity) || 0,
          warehouseId: effectiveWarehouseId ?? undefined,
        }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["skus"] });
      void queryClient.invalidateQueries({ queryKey: ["skus", sku.id] });
      void queryClient.invalidateQueries({ queryKey: ["purchases"] });
      void queryClient.invalidateQueries({ queryKey: ["sync-logs"] });
      toast.add({
        type: "success",
        title: "Saldo ajustado",
        description:
          "O novo saldo será enviado aos anúncios vinculados a este depósito.",
      });
      setOpen(false);
      setWarehouseId(null);
      setQuantity(null);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setCost(sku.cost != null ? String(sku.cost).replace(".", ",") : "");
          setLeadTimeDays(String(sku.leadTimeDays ?? 7));
          setSupplier(sku.supplier ?? "");
        } else {
          setWarehouseId(null);
          setQuantity(null);
        }
      }}
    >
      <DialogTrigger
        render={
          <Button variant="ghost" size="icon" title="Ajustar saldo">
            <PencilLine />
          </Button>
        }
      />
      <DialogContent
        title={`Ajustar — ${sku.code}`}
        description="Saldo do depósito, custo, prazo de reposição e fornecedor da lista de compras."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            adjust.mutate();
          }}
        >
          <Field>
            <FieldLabel>Depósito</FieldLabel>
            <WarehouseSelect
              warehouses={warehouseOptions}
              value={effectiveWarehouseId}
              onChange={(id) => {
                setWarehouseId(id);
                setQuantity(null);
              }}
            />
          </Field>
          <Field>
            <FieldLabel>Novo saldo</FieldLabel>
            <Input
              type="number"
              min="0"
              required
              value={effectiveQuantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel>Custo unitário (R$)</FieldLabel>
            <Input
              inputMode="decimal"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="Ex.: 12,50"
            />
          </Field>
          <Field>
            <FieldLabel>Prazo de reposição (dias)</FieldLabel>
            <Input
              type="number"
              min="1"
              max="90"
              required
              value={leadTimeDays}
              onChange={(e) => setLeadTimeDays(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel>Fornecedor (opcional)</FieldLabel>
            <Input
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              placeholder="Para agrupar a lista de compras"
              maxLength={80}
            />
          </Field>
          <Button
            type="submit"
            disabled={!effectiveWarehouseId}
            loading={adjust.isPending}
          >
            Salvar e sincronizar
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MapListingDialog({ sku }: { sku: Sku }) {
  const [open, setOpen] = useState(false);
  const [listingId, setListingId] = useState<string | null>(null);
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState("1");
  const warehouses = useWarehouses(open);
  const queryClient = useQueryClient();
  const toast = useToast();

  const listings = useQuery({
    queryKey: ["listings", null],
    queryFn: async () => unwrap(await api.listings.get({ query: {} })),
    enabled: open,
  });

  const warehouseOptions = warehouses.data?.warehouses ?? [];
  const effectiveWarehouseId = warehouseId ?? warehouseOptions[0]?.id ?? null;

  const map = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.skus({ id: sku.id }).mappings.post({
          listingId: listingId!,
          warehouseId: effectiveWarehouseId ?? undefined,
          quantity: Number(quantity) || 1,
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["skus"] });
      void queryClient.invalidateQueries({ queryKey: ["listings"] });
      toast.add({ type: "success", title: "Anúncio vinculado ao SKU" });
      setOpen(false);
      setListingId(null);
      setWarehouseId(null);
      setQuantity("1");
    },
    onError: () => {
      toast.add({
        type: "error",
        title: "Não foi possível vincular",
        description: "Este SKU pode já estar neste anúncio.",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button variant="ghost" size="icon" title="Vincular anúncio">
            <Link2 />
          </Button>
        }
      />
      <DialogContent
        title={`Vincular anúncio — ${sku.code}`}
        description="O anúncio passa a consumir este SKU no depósito. Quantidade > 1 monta kit (várias unidades ou vários SKUs no mesmo anúncio)."
      >
        <div className="flex flex-col gap-4">
          <Field>
            <FieldLabel>Anúncio</FieldLabel>
            <Select
              value={listingId}
              itemToStringLabel={(id) => {
                const listing = listings.data?.listings.find((row) => row.id === id);
                return listing
                  ? `${listing.accountNickname} — ${listing.title}`
                  : "Selecione um anúncio";
              }}
              onValueChange={(v) => setListingId(v as string | null)}
            >
              <SelectTrigger
                placeholder={
                  listings.isLoading ? "Carregando..." : "Selecione um anúncio"
                }
                className="w-full"
              />
              <SelectContent>
                {listings.data?.listings.map((listing) => (
                  <SelectItem key={listing.id} value={listing.id}>
                    {`${listing.accountNickname} — ${listing.title}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel>Depósito</FieldLabel>
            <WarehouseSelect
              warehouses={warehouseOptions}
              value={effectiveWarehouseId}
              onChange={setWarehouseId}
            />
          </Field>
          <Field>
            <FieldLabel>Unidades por venda</FieldLabel>
            <Input
              type="number"
              min="1"
              required
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
            />
          </Field>
          <Button
            disabled={!listingId || !effectiveWarehouseId || Number(quantity) < 1}
            loading={map.isPending}
            onClick={() => map.mutate()}
          >
            Vincular
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CountDialog() {
  const [open, setOpen] = useState(false);
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<{
    adjusted: number;
    plus: number;
    minus: number;
    diffs: Array<{
      skuId: string;
      code: string;
      counted: number;
      current: number;
      delta: number;
    }>;
  } | null>(null);
  const warehouses = useWarehouses(open);
  const queryClient = useQueryClient();
  const toast = useToast();
  const warehouseOptions = warehouses.data?.warehouses ?? [];
  const effectiveWarehouseId = warehouseId ?? warehouseOptions[0]?.id ?? null;

  const sheet = useQuery({
    queryKey: ["stock-count", effectiveWarehouseId],
    queryFn: async () =>
      unwrap(
        await api.stock.count.get({
          query: { warehouseId: effectiveWarehouseId! },
        }),
      ),
    enabled: open && Boolean(effectiveWarehouseId),
  });

  const countedItems = Object.entries(counts)
    .map(([skuId, value]) => ({
      skuId,
      counted: Number(value),
    }))
    .filter((row) => row.counted >= 0 && counts[row.skuId] !== "");

  const run = useMutation({
    mutationFn: async (apply: boolean) =>
      unwrap(
        await api.stock.count.post({
          warehouseId: effectiveWarehouseId!,
          apply,
          items: countedItems,
        }),
      ),
    onSuccess: (result, apply) => {
      if (!apply) {
        setPreview(result);
        if (result.adjusted === 0) {
          toast.add({
            type: "success",
            title: "Contagem bateu",
            description: "Nada a ajustar neste depósito.",
          });
        }
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["skus"] });
      toast.add({
        type: "success",
        title: "Inventário aplicado",
        description: `${result.adjusted} SKU${result.adjusted === 1 ? "" : "s"} no ledger.`,
      });
      setOpen(false);
      setCounts({});
      setPreview(null);
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível conferir",
        description:
          error instanceof Error ? error.message : "Tente novamente.",
      });
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setCounts({});
          setPreview(null);
          setWarehouseId(null);
        }
      }}
    >
      <DialogTrigger
        render={
          <Button variant="secondary">
            <ClipboardList />
            Inventariar
          </Button>
        }
      />
      <DialogContent
        title="Inventário"
        description="Lista cega: conte o que está na prateleira. O saldo do sistema só aparece na conferência."
      >
        <div className="flex flex-col gap-4">
          <Field>
            <FieldLabel>Depósito</FieldLabel>
            <WarehouseSelect
              warehouses={warehouseOptions}
              value={effectiveWarehouseId}
              onChange={(id) => {
                setWarehouseId(id);
                setCounts({});
                setPreview(null);
              }}
            />
          </Field>
          {sheet.isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner className="size-4 text-ink-muted" />
            </div>
          ) : (
            <ul className="max-h-72 divide-y divide-line overflow-y-auto rounded-control border border-line">
              {sheet.data && "skus" in sheet.data
                ? sheet.data.skus.map((sku) => (
                    <li
                      key={sku.id}
                      className="flex items-center gap-3 px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-mono text-2xs font-medium text-ink">
                          {sku.code}
                        </p>
                        <p className="truncate text-2xs text-ink-muted">
                          {sku.title}
                        </p>
                      </div>
                      <Input
                        type="number"
                        min="0"
                        inputMode="numeric"
                        placeholder="—"
                        value={counts[sku.id] ?? ""}
                        onChange={(event) => {
                          setPreview(null);
                          setCounts((current) => ({
                            ...current,
                            [sku.id]: event.target.value,
                          }));
                        }}
                        className="w-20 text-right font-mono"
                      />
                    </li>
                  ))
                : null}
            </ul>
          )}
          {preview && preview.adjusted > 0 ? (
            <div className="rounded-control border border-line bg-surface-sunken px-3 py-2">
              <p className="font-mono text-xl font-medium tracking-tight text-ink">
                {preview.adjusted}
                <span className="ml-1.5 text-[0.8125rem] font-normal text-ink-muted">
                  ajuste{preview.adjusted === 1 ? "" : "s"}
                </span>
              </p>
              <p className="text-2xs text-ink-muted">
                {formatDelta(preview.plus)} / {formatDelta(preview.minus)}
              </p>
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              disabled={!effectiveWarehouseId || countedItems.length === 0}
              loading={run.isPending && run.variables === false}
              onClick={() => run.mutate(false)}
            >
              Conferir
            </Button>
            <Button
              disabled={!preview || preview.adjusted === 0}
              loading={run.isPending && run.variables === true}
              onClick={() => run.mutate(true)}
            >
              Aplicar no ledger
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog() {
  const [open, setOpen] = useState(false);
  const [skuId, setSkuId] = useState<string | null>(null);
  const [fromWarehouseId, setFromWarehouseId] = useState<string | null>(null);
  const [toWarehouseId, setToWarehouseId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState("1");
  const warehouses = useWarehouses(open);
  const skus = useQuery({
    queryKey: ["skus"],
    queryFn: async () => unwrap(await api.skus.get()),
    enabled: open,
  });
  const queryClient = useQueryClient();
  const toast = useToast();
  const warehouseOptions = warehouses.data?.warehouses ?? [];
  const skuOptions = skus.data && "skus" in skus.data ? skus.data.skus : [];
  const fromId = fromWarehouseId ?? warehouseOptions[0]?.id ?? null;
  const toId =
    toWarehouseId ??
    warehouseOptions.find((row) => row.id !== fromId)?.id ??
    null;

  const transfer = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.skus({ id: skuId! }).transfer.post({
          fromWarehouseId: fromId!,
          toWarehouseId: toId!,
          quantity: Number(quantity) || 0,
        }),
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["skus"] });
      toast.add({
        type: "success",
        title: "Transferido",
        description:
          "quantity" in result ? `${result.quantity} un. mudaram de depósito.` : undefined,
      });
      setOpen(false);
      setSkuId(null);
      setQuantity("1");
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível transferir",
        description:
          error instanceof Error ? error.message : "Tente novamente.",
      });
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSkuId(null);
          setFromWarehouseId(null);
          setToWarehouseId(null);
          setQuantity("1");
        }
      }}
    >
      <DialogTrigger
        render={
          <Button variant="secondary">
            <ArrowLeftRight />
            Transferir
          </Button>
        }
      />
      <DialogContent
        title="Transferir"
        description="Sai de um depósito e entra no outro, com duas linhas no histórico."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            transfer.mutate();
          }}
        >
          <Field>
            <FieldLabel>SKU</FieldLabel>
            <Select
              value={skuId}
              itemToStringLabel={(id) => {
                const sku = skuOptions.find((row) => row.id === id);
                return sku ? `${sku.code} — ${sku.title}` : "Selecione";
              }}
              onValueChange={(value) => setSkuId(value as string | null)}
            >
              <SelectTrigger placeholder="Selecione um SKU" className="w-full" />
              <SelectContent>
                {skuOptions.map((sku) => (
                  <SelectItem key={sku.id} value={sku.id}>
                    {sku.code} — {sku.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field>
              <FieldLabel>De</FieldLabel>
              <WarehouseSelect
                warehouses={warehouseOptions}
                value={fromId}
                onChange={setFromWarehouseId}
              />
            </Field>
            <Field>
              <FieldLabel>Para</FieldLabel>
              <WarehouseSelect
                warehouses={warehouseOptions}
                value={toId}
                onChange={setToWarehouseId}
              />
            </Field>
          </div>
          <Field>
            <FieldLabel>Quantidade</FieldLabel>
            <Input
              type="number"
              min="1"
              required
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
            />
          </Field>
          <Button
            type="submit"
            disabled={!skuId || !fromId || !toId || fromId === toId}
            loading={transfer.isPending}
          >
            Transferir
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const CSV_TEMPLATE =
  "codigo;titulo;custo;quantidade;fornecedor;prazo\nCAM-PRETA-M;Camiseta preta M;29,90;12;Fornecedor A;7\n";

function ImportCsvDialog() {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState<{
    creates: number;
    updates: number;
    skipped: number;
    rows: Array<{
      line: number;
      code: string;
      title: string | null;
      action: string;
      error: string | null;
    }>;
  } | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();

  const run = useMutation({
    mutationFn: async (apply: boolean) =>
      unwrap(await api.skus.import.post({ csv, apply })),
    onSuccess: (result, apply) => {
      setPreview(result);
      if (!apply) return;
      void queryClient.invalidateQueries({ queryKey: ["skus"] });
      void queryClient.invalidateQueries({ queryKey: ["purchases"] });
      toast.add({
        type: "success",
        title: "CSV importado",
        description: `${result.creates} novos · ${result.updates} atualizados.`,
      });
      setOpen(false);
      setCsv("");
      setPreview(null);
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível ler o CSV",
        description:
          error instanceof Error ? error.message : "Confira o cabeçalho.",
      });
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setCsv("");
          setPreview(null);
        }
      }}
    >
      <DialogTrigger
        render={
          <Button variant="secondary">
            <FileSpreadsheet />
            CSV
          </Button>
        }
      />
      <DialogContent
        title="Importar CSV"
        description="Custos e SKUs em massa. Cabeçalho: codigo;titulo;custo;quantidade;fornecedor;prazo."
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2">
            <Field className="flex-1">
              <FieldLabel>Arquivo</FieldLabel>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    setPreview(null);
                    setCsv(String(reader.result ?? ""));
                  };
                  reader.readAsText(file);
                }}
              />
            </Field>
            <button
              type="button"
              className="mt-5 text-2xs font-medium text-ember-strong transition-transform duration-150 ease-swift active:scale-[0.97]"
              onClick={() => {
                const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = "skus-forge.csv";
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              Baixar modelo
            </button>
          </div>
          {preview ? (
            <div className="rounded-control border border-line bg-surface-sunken px-3 py-2">
              <p className="font-mono text-xl font-medium tracking-tight text-ink">
                {preview.creates + preview.updates}
                <span className="ml-1.5 text-[0.8125rem] font-normal text-ink-muted">
                  linha{preview.creates + preview.updates === 1 ? "" : "s"}
                </span>
              </p>
              <p className="text-2xs text-ink-muted">
                {preview.creates} novos · {preview.updates} atualizar ·{" "}
                {preview.skipped} ignorados
              </p>
            </div>
          ) : null}
          {preview && preview.rows.length > 0 ? (
            <ul className="max-h-48 divide-y divide-line overflow-y-auto rounded-control border border-line">
              {preview.rows.slice(0, 40).map((row) => (
                <li
                  key={`${row.line}-${row.code}`}
                  className="flex items-center justify-between gap-2 px-3 py-1.5"
                >
                  <span className="truncate font-mono text-2xs text-ink">
                    {row.code || `linha ${row.line}`}
                  </span>
                  <span className="shrink-0 text-2xs text-ink-muted">
                    {row.error ??
                      (row.action === "create"
                        ? "Novo"
                        : row.action === "update"
                          ? "Atualizar"
                          : "Ignorar")}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              disabled={!csv.trim()}
              loading={run.isPending && run.variables === false}
              onClick={() => run.mutate(false)}
            >
              Conferir
            </Button>
            <Button
              disabled={!preview || preview.creates + preview.updates === 0}
              loading={run.isPending && run.variables === true}
              onClick={() => run.mutate(true)}
            >
              Importar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
